import isEqual from 'lodash.isequal';
import { DataFieldWithDataType } from '../../../../common-types';
import {
  $HierarchicalFilterSeparator$,
  $UndefinedValueIndicator$,
  ApiCustomSqlQueryResponse,
} from '../../../common/api/api-interfaces';
import { SQLFilters } from '../../../common/api/master-data-service';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  GroupingInput,
  MetricGroupId,
  MetricId,
  RegularMetricId,
  TimeSelectionInput,
} from '../../../common/graphql/generated/graphql-sdk';
import {
  getReactQueryWrappedService,
  V2Service,
} from '../../../common/services/utils';
import { isHierarchical, uuid } from '../../../common/utilFunctions/utils';
import {
  dataTypesToApiMasterDataTypeMapper,
  singleNonNullValueFilter,
  singleNullValueFilter,
  singleValueTimeSegment,
} from '../../../pages/dashboard/balance-sheet/backend-metrics/utils';
import {
  PbsByDimensionServiceInputs,
  PbsByDimensionServiceResponse,
} from '../../../pages/dashboard/balance-sheet/types';
import { CachedMasterDataService } from '../../api/cached-master-data-service';
import { VersionId } from '../../v2/api/types';
import { Filter } from '../../v2/common/components/filter/filterbar/types';
import { removeDuplicates } from '../../v2/common/utils';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../../v2/context/legacy-types';
import {
  ApiMasterDataQueryFilterItem,
  DataFields,
  DataTypes,
} from '../../v2/types';
import { TO_BE_DEPRECATED_getFilterConditionsForMetricGroup } from './utils';

export const getPbsByDimensionServiceForRegularBackendMetrics = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricGroupId: MetricGroupIdInstance,
  cachedMasterDataApiService: CachedMasterDataService
) => {
  return getPbsByDimensionServiceForBackendMetrics(
    metricGroupId,
    (
      timeSelection: TimeSelectionInput,
      metricIds: MetricId[],
      userFilters?: SQLFilters | null,
      grouping?: GroupingInput,
      disableNestLoop?: boolean
    ) =>
      cachedMasterDataApiService.queryMetrics(
        timeSelection,
        metricIds as unknown as RegularMetricId[],
        userFilters,
        grouping,
        disableNestLoop
      )
  );
};

export const getPbsByDimensionServiceForCohortBackendMetrics = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricId: MetricGroupIdInstance,
  cachedMasterDataApiService: CachedMasterDataService
) => {
  return getPbsByDimensionServiceForBackendMetrics(
    metricId,
    (
      timeSelection: TimeSelectionInput,
      metricIds: MetricId[],
      userFilters?: SQLFilters | null,
      grouping?: GroupingInput,
      disableNestLoop?: boolean
    ) =>
      cachedMasterDataApiService.queryCohortMetrics(
        timeSelection,
        metricIds as unknown as CohortMetricId[],
        userFilters,
        grouping,
        disableNestLoop
      )
  );
};

export const getPbsByDimensionServiceForEmployeeCohortBackendMetrics = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricGroupId: MetricGroupIdInstance,
  cachedMasterDataApiService: CachedMasterDataService
) => {
  return getPbsByDimensionServiceForBackendMetrics(
    metricGroupId,
    (
      timeSelection: TimeSelectionInput,
      metricIds: MetricId[],
      userFilters?: SQLFilters | null,
      grouping?: GroupingInput,
      disableNestLoop?: boolean
    ) =>
      cachedMasterDataApiService.queryEmployeeCohortMetrics(
        timeSelection,
        metricIds as unknown as EmployeeCohortMetricId[],
        userFilters,
        // TODO: Hardcoding userCohortFilters to undefined. Fix when merging with v2
        undefined,
        grouping,
        disableNestLoop
      )
  );
};

export type PbsByDimensionService = V2Service<
  PbsByDimensionServiceInputs,
  PbsByDimensionServiceResponse | null,
  ApiMasterDataQueryFilterItem
>;

const getPbsByDimensionServiceForBackendMetrics = <
  MetricGroupIdInstance extends MetricGroupId,
  ID extends RegularMetricId | CohortMetricId,
>(
  metricGroupId: MetricGroupId,
  queryMetrics: (
    timeSelection: TimeSelectionInput,
    metricsDimensions: MetricId[],
    userFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ) => Promise<ApiCustomSqlQueryResponse>
) => {
  const PbsByDimensionService: PbsByDimensionService = async (
    inputs: PbsByDimensionServiceInputs,
    filters: ApiMasterDataQueryFilterItem[],
    latestVersions: Partial<Record<DataTypes, VersionId>>,
    legacyStores: LegacyStore,
    legacyDomainScopedStores: LegacyDomainScopedStore
  ) => {
    const { metricStore } = legacyDomainScopedStores;
    const { timeSliderConfig, dimension } = inputs;
    const sqlUserFilters = TO_BE_DEPRECATED_getFilterConditionsForMetricGroup(
      filters as Filter[],
      metricGroupId,
      metricStore
    );
    const filtersBasedOnDimension = removeDuplicates(
      filters.filter((f) => {
        const field = {
          dataType: f.dataType,
          dataField: f.property as DataFields,
        };
        return isHierarchical(field) && isEqual(field, dimension);
      })
    );

    const groups = isHierarchical(dimension)
      ? filtersBasedOnDimension.length > 0
        ? [
            {
              byHierarchicalField: {
                dataField: dimension.dataField,
                dataType: dataTypesToApiMasterDataTypeMapper(
                  dimension.dataType
                ),
                filters: filtersBasedOnDimension.map((f) =>
                  f.values.flatMap((v) =>
                    Array.isArray(v)
                      ? v.map((h: any) =>
                          h === null
                            ? singleNullValueFilter()
                            : singleNonNullValueFilter(h, {
                                dataType: f.dataType,
                                dataField: f.property,
                              } as DataFieldWithDataType)
                        )
                      : v === null
                        ? singleNullValueFilter()
                        : singleNonNullValueFilter(v, {
                            dataType: f.dataType,
                            dataField: f.property,
                          } as DataFieldWithDataType)
                  )
                ),
              },
              byField: null,
            },
          ]
        : [
            {
              byField: {
                dataField: `${dimension.dataField}_LEVEL_1`,
                dataType: dataTypesToApiMasterDataTypeMapper(
                  dimension.dataType
                ),
                filters: null,
              },
              byHierarchicalField: null,
            },
          ]
      : [
          {
            byField: {
              dataField: dimension.dataField,
              dataType: dataTypesToApiMasterDataTypeMapper(dimension.dataType),
              filters: null,
            },
            byHierarchicalField: null,
          },
        ];
    const groupings = { groups };
    const metrics =
      metricStore.getAllowedSortedMetricIdsForMetricGroup(metricGroupId);
    const result = await queryMetrics(
      singleValueTimeSegment(timeSliderConfig),
      metrics.map((m) => m.id),
      sqlUserFilters,
      groupings
    );

    const response = result.dataPoints.reduce(
      (acc, dp) => {
        const label = dp.dimensions
          .map((d) => d.value ?? $UndefinedValueIndicator$)
          .join($HierarchicalFilterSeparator$);
        const value = dp.measures[0].value;
        const metric = metrics.find(
          (m) => m.id === (dp.measures[0].property as string)
        )!;
        return {
          dataMap: {
            ...acc.dataMap,
            [label]: {
              ...(acc?.dataMap?.[label] ?? {}),
              ...{ [metric.id]: value },
            },
          },
          labels: [...acc.labels, label],
        };
      },
      { dataMap: {} as Record<string, any>, labels: [] as string[] }
    );
    return response;
  };
  PbsByDimensionService.cacheKey = uuid();
  return getReactQueryWrappedService(PbsByDimensionService);
};
